/**
 * LA RELIURE : l'en-tête, les six écrans, le pied de page.
 *
 * L'en-tête est MINCE et il le reste : sur l'écran de lecture, chaque pixel pris en haut est un
 * pixel pris au texte (design §1.1). Il porte le mot, quatre liens, et la bascule de thème — rien
 * d'autre ne mérite d'être là à la quatre-vingtième heure de jeu. Le quatrième mène aux TROIS
 * volets du §8 : un lien par volet aurait fait de la boutique le tiers de la navigation.
 */
import { Defs } from "./composants/Defs.js";
import { BandeauHorsLigne } from "./composants/HorsLigne.js";
import { Logo } from "./composants/Logo.js";
import { Cimetiere } from "./ecrans/Cimetiere.js";
import { Aventures } from "./ecrans/Aventures.js";
import { CaisseLocale, Coffre, Echoppe, Souscription } from "./ecrans/Echoppe.js";
import { JournalPublie } from "./ecrans/JournalPublie.js";
import { Semaine } from "./ecrans/Semaine.js";
import { Creation } from "./ecrans/Creation.js";
import { FicheDuHeros } from "./ecrans/FicheDuHeros.js";
import { LesHeros } from "./ecrans/LesHeros.js";
import { Partie } from "./ecrans/Partie.js";
import { Porte } from "./ecrans/Porte.js";
import { Table } from "./ecrans/Table.js";
import { LANGUES, NOMS_DE_LANGUE, poseLaLangue, t, useLangue } from "./lexique/langue.js";
import { correspond, Lien, useChemin, va } from "./routeur.js";
import { useSession } from "./session.js";
import { useTheme } from "./theme.js";

/**
 * LA LANGUE, DANS L'ENSEIGNE (§13.2) — deux poignées pour un seul réglage, comme la lampe.
 *
 * Le pupitre est un tiroir de PARTIE : un lecteur qui n'en a pas ouvert une ne peut pas l'atteindre,
 * et c'est précisément lui qui a besoin de changer de langue — devant la porte, avant même d'avoir
 * un compte.
 *
 * DEUX LANGUES, DONC UNE BASCULE et pas une liste déroulante : le bouton porte le nom de l'AUTRE
 * langue, écrit DANS cette langue-là, ce qui est la seule façon qu'un lecteur reconnaisse la sienne
 * dans une interface qu'il ne sait pas encore lire. À trois langues, ce bouton redeviendra une
 * liste ; à deux, une liste serait un clic de plus pour rien.
 */
function LaLangue() {
  const courante = useLangue();
  const autre = LANGUES.find((code) => code !== courante) ?? courante;
  return (
    <button
      type="button"
      className="bouton bouton--discret"
      lang={autre}
      onClick={() => poseLaLangue(autre)}
      title={`${NOMS_DE_LANGUE[courante]} → ${NOMS_DE_LANGUE[autre]}`}
    >
      {NOMS_DE_LANGUE[autre]}
    </button>
  );
}

function Enseigne() {
  const [theme, bascule] = useTheme();
  const { compte, deconnecte } = useSession();

  return (
    <header className="enseigne">
      <Lien vers="/" className="enseigne__marque" aria-label={t("Destinia — accueil")}>
        <Logo hauteur={38} />
      </Lien>

      <nav className="enseigne__liens">
        {compte ? (
          <>
            <Lien vers="/heros">{t("Héros")}</Lien>
            <Lien vers="/aventures">{t("Aventures")}</Lien>
            {/* UN SEUL LIEN POUR LES TROIS VOLETS DU §8 (design §3). L'en-tête est mince et il le
                reste : sur l'écran de lecture, chaque pixel pris en haut est un pixel pris au
                texte, et trois liens pour vendre auraient fait de la boutique le tiers de la
                navigation d'un livre-jeu. */}
            <Lien vers="/echoppe">{t("Échoppe")}</Lien>
          </>
        ) : null}
        <Lien vers="/cimetiere">{t("Cimetière")}</Lien>
      </nav>

      <div className="rang rang--serre">
        <LaLangue />
        <button
          type="button"
          className="bouton bouton--discret"
          onClick={bascule}
          aria-pressed={theme === "veillee"}
          title={theme === "veillee" ? t("Repasser au parchemin") : t("Éteindre la lampe")}
        >
          {theme === "veillee" ? t("Parchemin") : t("Veillée")}
        </button>
        {compte ? (
          <button
            type="button"
            className="bouton bouton--discret"
            onClick={() => {
              void deconnecte().then(() => va("/"));
            }}
          >
            {t("Fermer le livre")}
          </button>
        ) : null}
      </div>
    </header>
  );
}

function Ecran() {
  const chemin = useChemin();
  const { compte, enChargement } = useSession();

  const partie = correspond("/partie/:id", chemin);
  const heros = correspond("/heros/:id", chemin);

  if (chemin === "/cimetiere") return <Cimetiere />;
  // L'AVENTURE DE LA SEMAINE SE LIT SANS COMPTE (§8.1) : c'est la vitrine du jeu, et la même
  // aventure pour tout le monde n'a rien de secret.
  if (chemin === "/semaine") return <Semaine />;

  // On ne montre RIEN d'un compte tant qu'on ne sait pas s'il y en a un : afficher la porte une
  // demi-seconde à un joueur déjà connecté, c'est lui dire qu'il a été déconnecté.
  if (enChargement) return <div className="page" aria-busy="true" />;

  if (!compte) return <Porte />;

  if (chemin === "/" ) return <Table />;
  if (chemin === "/heros") return <LesHeros />;
  if (chemin === "/heros/nouveau") return <Creation />;
  if (heros) return <FicheDuHeros herosId={heros.id!} />;
  if (chemin === "/aventures") return <Aventures />;
  if (chemin === "/echoppe") return <Echoppe />;
  if (chemin === "/coffre") return <Coffre />;
  if (chemin === "/souscription") return <Souscription />;
  if (chemin === "/paiement/guichet") return <CaisseLocale />;
  if (partie) return <Partie partieId={partie.id!} />;

  return (
    <div className="page page--etroite">
      <h1>{t("Cette page n'existe pas")}</h1>
      <p className="douce">{t("Le livre n'a pas ce feuillet. Revenez à la table.")}</p>
      <Lien vers="/" className="bouton">
        {t("La table")}
      </Lien>
    </div>
  );
}

export function App() {
  const chemin = useChemin();
  /* L'ABONNEMENT À LA LANGUE, ET IL SUFFIT (§13.2). Elle vit hors de React ; changer de langue
     réveille cet abonnement-ci, l'arbre entier se redessine, et chaque `t()` relit la nouvelle. Un
     contexte aurait obligé à la faire passer par tous les composants intermédiaires — y compris
     ceux qui n'affichent pas un mot. */
  useLangue();

  /* LE CARNET PARTAGÉ N'A NI RELIURE NI PIED DE PAGE (design §3). C'est un document : quelqu'un
     l'ouvre parce qu'un ami lui a dit « regarde ce qui m'est arrivé », pas pour découvrir un
     produit. Une barre de navigation et un bouton « se connecter » trahiraient la personne qui a
     partagé — d'où cette sortie AVANT la reliure, et non un `display: none` dedans. */
  const carnet = correspond("/carnet/:jeton", chemin);
  if (carnet) {
    return (
      <>
        <Defs />
        <main>
          <JournalPublie jeton={carnet.jeton!} />
        </main>
      </>
    );
  }

  return (
    <>
      <Defs />
      <Enseigne />
      <BandeauHorsLigne />
      <main>
        <Ecran />
      </main>
      <footer className="pied">
        <span className="capitales">{t("Destinia — le livre dont on ne sort qu'une fois")}</span>
        <span className="faible">
          {t("Aucun dé n'est lancé dans votre dos. Celui qui vous tuera non plus.")}
        </span>
      </footer>
    </>
  );
}
